import {
  DeliveryDetailsWidget,
  getIncotermsInfo,
  IncotermSelect,
  type IncotermsInfo,
  PurchaseRequestFlowStepper,
  setDeliveryDetailsSchemaShape,
} from "@fero/components";
import {
  useAppService,
  useGetStorageLocationsSupportsDeliveryCalculationsQuery,
  useIsFeatureEnabled,
} from "@fero/hooks";
import { PortalPageContent, PortalPageContentHeader } from "@fero/layouts";
import type {
  PurchaseRequestFlowState,
  StorageLocationProductSale,
} from "@fero/pages";
import { useTradeSalesStore } from "@fero/stores";
import { isEUCountry } from "@fero/utils";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  FormControl,
  FormLabel,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import type { Country } from "adl-gen/common";
import type {
  NominatedPurchaserDetailsView,
  StorageLocationView,
} from "adl-gen/ferovinum/app/api";
import {
  type CollectionIncoterms,
  type PurchaseRequestDeliveryOption,
  type SavedDeliveryInfo,
  type ShippingDelivery,
  snCollectionIncoterms,
  valuesCollectionIncoterms,
} from "adl-gen/ferovinum/app/db";
import type { AdditionalCost } from "adl-gen/ferovinum/app/nompurchaser";
import type { ScopedName } from "adl-gen/sys/adlast";
import { isSameFile } from "components/library/widgets/file-picker";
import { DeliveryInstructionDocUploadCard } from "components/library/widgets/wizard-card/delivery-instruction-doc-upload-card";
import { ExpandableRadio } from "components/widgets/inputs/expandable-radio/expandable-radio";
import { type FormikProps, useFormik } from "formik";
import type React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  getFormDescriptionForUnionField,
  getFormLabelForUnionField,
} from "utils/adl-utils";
import { parseLocalTimeToDate } from "utils/date-utils";
import { number, object, string } from "yup";
import { CustomCodesStep } from "./components/custom-codes-step";
import DeliveryFeeCard from "./components/delivery-fee-card";

function getIncotermsToDescriptionMap<T extends string>(
  options: T[],
  scopedName: ScopedName,
): Map<T, IncotermsInfo<T>> {
  return new Map(
    options.map((option) => {
      return [
        option,
        {
          code: option,
          label: getFormLabelForUnionField(scopedName, option),
          description: getFormDescriptionForUnionField(scopedName, option),
          disabled: false,
        },
      ];
    }),
  );
}

function getDestinationCountry(
  deliveryCountry: Country | null,
  purchaserCountry: Country | null,
  deliveryType: DeliveryOptionType,
) {
  switch (deliveryType) {
    case "collectFromStorageLocation":
      return purchaserCountry;
    case "deliveryToNominatedPurchaser":
      return deliveryCountry;
    default:
      return undefined;
  }
}

const SETUP_STEPS = [
  {
    title: "Collection Date",
    question:
      "Please specify how long it will take to prepare the stock for collection / delivery?",
  },
  {
    title: "Customer Order Number",
  },
  {
    title: "Shipment Number",
  },
  {
    title: "Delivery Method",
    question: "Please select your preferred delivery option.",
  },
  {
    title: "Delivery Fees",
    question: "Please indicate delivery fees to be charged to the purchaser.",
  },
];

export enum PaymentResponsibilityOption {
  Purchaser = "Purchaser",
  Seller = "Seller",
  PurchaserCustom = "PurchaserCustom",
}

function getPaymentResponsibilityOptionKeys(
  showCustomAndAdditionalDeliveryCosts: boolean,
): (keyof typeof PaymentResponsibilityOption)[] {
  return (
    Object.keys(
      PaymentResponsibilityOption,
    ) as (keyof typeof PaymentResponsibilityOption)[]
  ).filter(
    (key) => showCustomAndAdditionalDeliveryCosts || key !== "PurchaserCustom",
  );
}

type DeliveryOptionType = PurchaseRequestDeliveryOption["kind"];

export type PreparationDaysType = "immediate" | "days";

export interface DeliveryPurchaserRequestFormValues {
  customerPoRef?: string;
  shipmentNumber?: string;
  preparationDaysType: PreparationDaysType;
  preparationDays?: number;
  deliveryType: DeliveryOptionType;
  collectionIncoterms?: CollectionIncoterms;
  deliveryFeePaidBy?: keyof typeof PaymentResponsibilityOption;
  deliveryDetails: DeliveryDetailsForm;
  deliveryInstructionDocs?: File[];
  customDeliveryCost?: number;
  additionalDeliveryCost?: AdditionalCost;
}

interface DeliveryDetailsForm
  extends Omit<
    ShippingDelivery,
    "deliveryDate" | "deliveryTimeEarliest" | "deliveryTimeLatest"
  > {
  deliveryDate: Date | null;
  deliveryTimeEarliest: Date | null;
  deliveryTimeLatest: Date | null;
}

export interface OrganisationPurchaseRequestDeliveryOptionsPageViewProps {
  purchaser: NominatedPurchaserDetailsView;
  storageLocation: StorageLocationView;
  deliveryIsAvailable: boolean;
  hasProductionPlanAttached: boolean;
  isBondedSale: boolean;
  onNext: (values: DeliveryPurchaserRequestFormValues) => Promise<void>;
  onBack: () => void;
  initialValues?: PurchaseRequestFlowState["delivery"];
  products?: StorageLocationProductSale[];
}

const collectionIncoterms: Map<
  CollectionIncoterms,
  IncotermsInfo<CollectionIncoterms>
> = getIncotermsToDescriptionMap(
  valuesCollectionIncoterms,
  snCollectionIncoterms,
);

const BONDED_DELIVERY_INCOTERMS = getIncotermsInfo(["DAP", "CFR"]);
const OUT_OF_BOND_DELIVERY_INCOTERMS = getIncotermsInfo(["DDP"]);

export const OrganisationPurchaseRequestDeliveryOptionsPageView = ({
  purchaser,
  storageLocation,
  deliveryIsAvailable,
  hasProductionPlanAttached,
  isBondedSale,
  onNext,
  onBack,
  initialValues,
  products,
}: OrganisationPurchaseRequestDeliveryOptionsPageViewProps) => {
  const appService = useAppService();
  const showCustomAndAdditionalDeliveryCosts = useIsFeatureEnabled(
    "trade_sale_custom_and_additional_delivery_costs",
  );
  const { data: storageLocationSupportsDeliveryCalculations } =
    useGetStorageLocationsSupportsDeliveryCalculationsQuery(storageLocation.id);

  const {
    options: { isEditMode },
  } = useTradeSalesStore();
  const [savedDeliveryInfoList, setSavedDeliveryInfoList] = useState<
    SavedDeliveryInfo[]
  >([]);
  useEffect(() => {
    appService
      .getNominatedPurchaserSavedDeliveryInfoList(purchaser)
      .then(setSavedDeliveryInfoList);
  }, [appService, purchaser]);
  const validationSchema = useMemo(
    () =>
      object().shape({
        customerPoRef: string().trim().optional().max(20),
        shipmentNumber: string().trim().optional().max(50),
        preparationDaysType: string().oneOf(["immediate", "days"]).required(),
        preparationDays: number()
          .when("preparationDaysType", {
            is: "immediate",
            then: (schema) => schema.test((n) => n === 0),
            otherwise: (schema) =>
              schema.positive("Please input a number more than 0"),
          })
          .required("This is a required field"),
        deliveryType: string()
          .oneOf(["collectFromStorageLocation", "deliveryToNominatedPurchaser"])
          .required(),
        collectionIncoterms: string().when("deliveryType", {
          is: (deliveryType: DeliveryOptionType) =>
            deliveryType === "collectFromStorageLocation",
          then: (schema) => schema.oneOf(valuesCollectionIncoterms).required(),
        }),
        deliveryFeePaidBy: string().when("deliveryType", {
          is: (deliveryType: DeliveryOptionType) =>
            deliveryType === "deliveryToNominatedPurchaser",
          then: (schema) =>
            schema.oneOf(Object.keys(PaymentResponsibilityOption)).required(),
        }),
        customDeliveryCost: number().when("deliveryFeePaidBy", {
          is: PaymentResponsibilityOption.PurchaserCustom,
          then: (schema) => schema.required("Custom delivery fee is required"),
        }),
        deliveryDetails: object().when("deliveryType", {
          is: (deliveryType: DeliveryOptionType) =>
            deliveryType === "deliveryToNominatedPurchaser",
          then: (schema) =>
            setDeliveryDetailsSchemaShape(
              schema,
              storageLocation.deliveryInstructionConfig,
              [
                ...BONDED_DELIVERY_INCOTERMS,
                ...OUT_OF_BOND_DELIVERY_INCOTERMS,
              ].map((t) => t.code),
              isEditMode,
            ),
        }),
      }),

    [storageLocation.deliveryInstructionConfig, isEditMode],
  );

  const onSubmit = useCallback(
    async (formValues: DeliveryPurchaserRequestFormValues) => {
      //Note(Berto): Beware of .trim() and other transformations functionalities in yup, it will only apply the transformations
      // for validation purposes and not for the actual submitted values
      // We need to manually cast the submitted values in order to apply yup transformations
      const values = validationSchema.cast(
        formValues,
      ) as unknown as DeliveryPurchaserRequestFormValues;
      return await onNext(values);
    },
    [onNext, validationSchema],
  );

  const form = useFormik<DeliveryPurchaserRequestFormValues>({
    initialValues: getInitialFormValues(
      initialValues,
      hasProductionPlanAttached,
    ),
    validationSchema,
    validateOnMount: true,
    onSubmit,
  });

  const shouldDisplayHSCodeTable = () => {
    const sourceCountry = storageLocation.country;
    const destinationCountry = getDestinationCountry(
      form.values.deliveryDetails.country,
      purchaser.purchaserCountry,
      form.values.deliveryType,
    );
    if (!sourceCountry || !destinationCountry) {
      return false;
    }

    if (sourceCountry === destinationCountry) {
      return false;
    }

    const sourceIsEU = isEUCountry(sourceCountry);
    const destinationIsEU = isEUCountry(destinationCountry);
    return sourceIsEU !== destinationIsEU || (!sourceIsEU && !destinationIsEU);
  };

  return (
    <PortalPageContent
      header={
        <OrganisationPurchaseRequestDeliveryHeader
          purchaserName={purchaser.name}
          hasProductionPlanAttached={hasProductionPlanAttached}
        />
      }
    >
      <Stack spacing={5}>
        <Stack alignItems="flex-start">
          <DeliveryStep {...SETUP_STEPS[0]}>
            {hasProductionPlanAttached && !isEditMode ? (
              <TextField
                label="Days required"
                name={"preparationDays"}
                required
                type="number"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">Days</InputAdornment>
                  ),
                }}
                value={form.values.preparationDays}
                error={
                  form.touched.preparationDays &&
                  Boolean(form.errors.preparationDays)
                }
                onBlur={form.handleBlur}
                onChange={form.handleChange}
                helperText={
                  form.touched.preparationDays && form.errors.preparationDays
                }
              />
            ) : (
              <ExpandableRadio
                value={form.values.preparationDaysType}
                onChange={(preparationDaysType) => {
                  form.setFieldValue(
                    "preparationDaysType",
                    preparationDaysType,
                  );

                  if (preparationDaysType === "immediate") {
                    form.setFieldValue("preparationDays", 0);
                  } else {
                    form.setFieldValue("preparationDays", "");
                  }
                }}
                options={[
                  {
                    value: "immediate",
                    label:
                      "The stock will be ready for collection/delivery immediately",
                    disabled: isEditMode,
                  },
                  {
                    value: "days",
                    label:
                      "The stock will not be ready for collection/delivery immediately",
                    disabled: isEditMode,
                    children: (
                      <TextField
                        label="Days required"
                        name={"preparationDays"}
                        required
                        type="number"
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">Days</InputAdornment>
                          ),
                        }}
                        value={form.values.preparationDays}
                        error={
                          form.touched.preparationDays &&
                          Boolean(form.errors.preparationDays)
                        }
                        onBlur={form.handleBlur}
                        onChange={form.handleChange}
                        helperText={
                          form.touched.preparationDays &&
                          form.errors.preparationDays
                        }
                      />
                    ),
                  },
                ]}
              />
            )}
          </DeliveryStep>
        </Stack>
        <DeliveryStep {...SETUP_STEPS[1]}>
          <TextField
            name="customerPoRef"
            label="Customer Order Number"
            value={form.values.customerPoRef}
            error={
              (form.touched.customerPoRef ?? false) &&
              Boolean(form.errors.customerPoRef ?? false)
            }
            inputProps={{ maxLength: 20 }}
            onChange={form.handleChange}
            onBlur={form.handleBlur}
          />
        </DeliveryStep>
        <DeliveryStep {...SETUP_STEPS[2]}>
          <TextField
            name="shipmentNumber"
            label="Shipment Number"
            value={form.values.shipmentNumber}
            error={
              (form.touched.shipmentNumber ?? false) &&
              Boolean(form.errors.shipmentNumber ?? false)
            }
            inputProps={{ maxLength: 50 }}
            onChange={form.handleChange}
            onBlur={form.handleBlur}
          />
        </DeliveryStep>
        <DeliveryStep {...SETUP_STEPS[3]}>
          {purchaser.autoAcceptTradeSale &&
            form.values.deliveryType === "collectFromStorageLocation" && (
              <Alert severity="warning">
                <AlertTitle>Note</AlertTitle>
                This order will only be released once the purchaser provides
                collection details, requested automatically via email on order
                creation.
              </Alert>
            )}
          <ExpandableRadio
            value={form.values.deliveryType}
            onChange={(deliveryType) =>
              form.setFieldValue("deliveryType", deliveryType)
            }
            options={[
              {
                value: "collectFromStorageLocation",
                label: `Collect from ${storageLocation.storageLocationName}`,
                disabled: isEditMode,
                children: (
                  <Grid container>
                    <Grid item xs={12} lg={6}>
                      <Stack spacing={2}>
                        <FormLabel component="legend">Incoterms</FormLabel>
                        <IncotermSelect
                          disabled={isEditMode}
                          direction="column"
                          value={form.values.collectionIncoterms}
                          incotermsToDescriptionMap={collectionIncoterms}
                          onChange={(v: string) =>
                            form.setFieldValue("collectionIncoterms", v)
                          }
                        />
                      </Stack>
                    </Grid>
                  </Grid>
                ),
              },
              {
                value: "deliveryToNominatedPurchaser",
                label: `Specify an address to deliver to${
                  deliveryIsAvailable
                    ? ""
                    : " - Currently unavailable at your selected storage location."
                }`,
                disabled: !deliveryIsAvailable || isEditMode,
                children: (
                  <ShippingLocationForm
                    form={form}
                    isBondedSale={isBondedSale}
                    savedDeliveryInfoList={savedDeliveryInfoList}
                    storageLocation={storageLocation}
                  />
                ),
              },
            ]}
          />
          {shouldDisplayHSCodeTable() && (
            <CustomCodesStep products={products} />
          )}
        </DeliveryStep>
        {showCustomAndAdditionalDeliveryCosts &&
          form.values.deliveryType === "deliveryToNominatedPurchaser" && (
            <DeliveryStep {...SETUP_STEPS[4]}>
              <DeliveryFeeCard
                form={form}
                purchaser={purchaser}
                storageLocationSupportsDeliveryCalculations={
                  storageLocationSupportsDeliveryCalculations
                }
                storageLocation={storageLocation}
              />
            </DeliveryStep>
          )}
        <Stack spacing={2} direction="row" justifyContent={"flex-end"}>
          <Button variant="outlined" onClick={onBack}>
            Back
          </Button>
          <Button onClick={form.submitForm} disabled={!form.isValid}>
            Next
          </Button>
        </Stack>
      </Stack>
    </PortalPageContent>
  );
};

const OrganisationPurchaseRequestDeliveryHeader = ({
  purchaserName,
  hasProductionPlanAttached,
}: {
  purchaserName: string;
  hasProductionPlanAttached: boolean;
}) => {
  const {
    options: { isEditMode },
  } = useTradeSalesStore();
  const title = (
    <Stack>
      <Typography variant={"h4"}>
        {isEditMode ? "Edit" : "New"} Trade Sale
      </Typography>
      <Typography>from {purchaserName}</Typography>
    </Stack>
  );
  return (
    <PortalPageContentHeader
      variant="split"
      title={title}
      right={
        <PurchaseRequestFlowStepper
          activeStep={hasProductionPlanAttached ? 2 : 1}
          withProductionPlan={hasProductionPlanAttached}
        />
      }
    />
  );
};

const DeliveryStep = ({
  title,
  question,
  children,
}: {
  title: string;
  question?: string;
  children: React.ReactNode;
}) => {
  return (
    <Stack
      spacing={2}
      sx={{ backgroundColor: "white", borderRadius: 1 }}
      padding={4}
      width={"100%"}
    >
      <Typography variant="h6">{title}</Typography>
      <Typography variant="body1">{question}</Typography>
      {children}
    </Stack>
  );
};

const today = new Date();
today.setHours(0, 0, 0, 0);

const ShippingLocationForm = ({
  form,
  isBondedSale,
  savedDeliveryInfoList,
  storageLocation,
}: {
  form: FormikProps<DeliveryPurchaserRequestFormValues>;
  isBondedSale: boolean;
  savedDeliveryInfoList: SavedDeliveryInfo[];
  storageLocation: StorageLocationView;
}) => {
  const {
    options: { isEditMode },
  } = useTradeSalesStore();
  const showCustomAndAdditionalDeliveryCosts = useIsFeatureEnabled(
    "trade_sale_custom_and_additional_delivery_costs",
  );

  return (
    <Stack spacing={3} direction="column">
      {!showCustomAndAdditionalDeliveryCosts && (
        <Box maxWidth={{ xs: "100%", lg: "50%" }} mx="auto">
          <Stack spacing={2} pr={{ xs: 0, lg: 4 }}>
            <FormLabel>Delivery Payment Responsibility</FormLabel>
            <FormControl
              fullWidth
              required
              disabled={isEditMode}
              error={Boolean(
                form.touched.deliveryFeePaidBy && form.errors.deliveryFeePaidBy,
              )}
            >
              <InputLabel>Select Responsible Party</InputLabel>
              <Select
                disabled={isEditMode}
                value={form.values.deliveryFeePaidBy ?? ""}
                label="Delivery Payment Responsibility"
                onChange={(e) =>
                  form.setFieldValue("deliveryFeePaidBy", e.target.value)
                }
              >
                {getPaymentResponsibilityOptionKeys(
                  showCustomAndAdditionalDeliveryCosts,
                ).map((key) => (
                  <MenuItem key={key} value={PaymentResponsibilityOption[key]}>
                    {PaymentResponsibilityOption[key]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </Box>
      )}
      <DeliveryDetailsWidget
        form={form}
        formKey="deliveryDetails"
        disabled={isEditMode}
        storageLocationName={storageLocation.storageLocationName}
        deliveryCountries={storageLocation.deliveryCountries}
        deliveryInstructionConfig={storageLocation.deliveryInstructionConfig}
        savedDeliveryInfoList={savedDeliveryInfoList}
        incoterms={{
          values: isBondedSale
            ? BONDED_DELIVERY_INCOTERMS
            : OUT_OF_BOND_DELIVERY_INCOTERMS,
        }}
        overwriteInfo={{
          incoterms: (
            <Stack spacing={1}>
              <Typography variant="subtitle1Bold">
                Which one should I choose?
              </Typography>
              <Typography>
                For nation-wide deliveries where you are selling to the
                third-party purchaser on a delivered basis out of bond (i.e.
                inclusive of delivery, duty & VAT) please select DDP. <br />
                <br />
                For bond-to-bond deliveries, in-bond warehouse transfers and
                export sales where you are selling to the third party purchaser
                on a delivered basis (i.e. where you are shipping to the
                destination for them) please select DAP.
              </Typography>
            </Stack>
          ),
        }}
      />

      <Stack spacing={3} flex={1} width={"100%"}>
        <DeliveryInstructionDocUploadCard
          fileValues={form.values.deliveryInstructionDocs ?? []}
          onFilesAccepted={(acceptedFiles) => {
            const existingFiles = form.values.deliveryInstructionDocs ?? [];
            const newFiles = acceptedFiles.filter(
              (f) =>
                existingFiles.findIndex((file) => isSameFile(file, f)) === -1,
            );
            if (newFiles.length > 0) {
              form.setFieldValue("deliveryInstructionDocs", [
                ...existingFiles,
                ...newFiles,
              ]);
            }
          }}
          removeFileAt={(atIdx) => {
            const files = form.values.deliveryInstructionDocs ?? [];
            const filtered = files.filter((_, idx) => idx !== atIdx);
            form.setFieldValue("deliveryInstructionDocs", filtered);
          }}
        />
      </Stack>
    </Stack>
  );
};

function getInitialFormValues(
  val: PurchaseRequestFlowState["delivery"],
  hasProductionPlanAttached: boolean,
): DeliveryPurchaserRequestFormValues {
  const deliveryDetails: DeliveryDetailsForm = {
    deliveryPointName: "",
    streetName: "",
    addressLine1: "",
    addressLine2: "",
    town: "",
    postCode: "",
    country: "GB",
    state: null,
    deliveryContact: "",
    deliveryContactEmail: "",
    deliveryContactNumber: "",
    deliveryDate: null,
    deliveryTimeEarliest: null,
    deliveryTimeLatest: null,
    deliveryInstructions: "",
    deliveryIncoterms: null,
    // this is ignored and will be overwritten by value on the setup page
    bonded: false,
  };

  const deliveryFeePaidBy = (() => {
    if (val?.purchaserCoversDeliveryCost) {
      return val?.customDeliveryCost?.amount
        ? PaymentResponsibilityOption.PurchaserCustom
        : PaymentResponsibilityOption.Purchaser;
    }
    if (val?.purchaserCoversDeliveryCost === false) {
      return PaymentResponsibilityOption.Seller;
    }
    return undefined;
  })();

  return {
    preparationDaysType:
      val?.preparationDaysType ??
      (hasProductionPlanAttached ? "days" : "immediate"),
    preparationDays:
      val?.preparationDays ?? (hasProductionPlanAttached ? undefined : 0),
    deliveryType: val?.deliveryOption?.kind ?? "collectFromStorageLocation",
    collectionIncoterms:
      val?.deliveryOption.kind === "collectFromStorageLocation"
        ? val.deliveryOption.value.incoterms
        : undefined,
    deliveryFeePaidBy,
    customerPoRef: val?.customerPoRef,
    shipmentNumber: val?.shipmentNumber,
    deliveryDetails:
      val?.deliveryOption.kind === "deliveryToNominatedPurchaser"
        ? {
            ...val.deliveryOption.value.deliveryDetails,
            deliveryContact:
              val.deliveryOption.value.deliveryDetails.deliveryContact ?? "",
            deliveryContactEmail:
              val.deliveryOption.value.deliveryDetails.deliveryContactEmail ??
              "",
            deliveryContactNumber:
              val.deliveryOption.value.deliveryDetails.deliveryContactNumber ??
              "",
            deliveryDate: val.deliveryOption.value.deliveryDetails.deliveryDate
              ? new Date(val.deliveryOption.value.deliveryDetails.deliveryDate)
              : null,
            deliveryTimeEarliest: val.deliveryOption.value.deliveryDetails
              .deliveryTimeEarliest
              ? parseLocalTimeToDate(
                  val.deliveryOption.value.deliveryDetails.deliveryTimeEarliest,
                )
              : null,
            deliveryTimeLatest: val.deliveryOption.value.deliveryDetails
              .deliveryTimeLatest
              ? parseLocalTimeToDate(
                  val.deliveryOption.value.deliveryDetails.deliveryTimeLatest,
                )
              : null,
            deliveryInstructions:
              val.deliveryOption.value.deliveryDetails.deliveryInstructions ??
              "",
          }
        : deliveryDetails,
    deliveryInstructionDocs: val?.deliveryInstructionDocs,
    customDeliveryCost: val?.customDeliveryCost
      ? Number(val.customDeliveryCost.amount)
      : undefined,
    additionalDeliveryCost: val?.additionalDeliveryCost,
  };
}
